import SnackbarContent from "@material-ui/core/SnackbarContent";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Badge from "@mui/material/Badge";
import BottomNavigation from "@mui/material/BottomNavigation";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import AuthService from "../Services/auth.service";
import LeaseService from "../Services/lease.service";
import MaintenanceRequestService from "../Services/maintenanceRequest.service";
import NotificationService from "../Services/notification.service";
import PaymentService from "../Services/payment.service";

const Navbar = ({
  handleNotificationClick,
  handleLogout,
  handleLanguageChange,
  i18n,
  userInfo,
  logoutLoading,
  unreadCount,
}) => {
  const [language, setLanguage] = useState(i18n.language);
  const { t } = useTranslation();

  const handleChangeLanguage = (e) => {
    const selectedLanguage = e.target.value;
    setLanguage(selectedLanguage);
    i18n.changeLanguage(selectedLanguage);
    handleLanguageChange(selectedLanguage);
  };

  return (
    <nav
      className="bg-gradient-to-r from-blue-600 via-blue-400 to-blue-700 p-4 w-full min-w-fit"
      style={{ backgroundColor: "rgba(100, 78, 55, 0.9)" }}
    >
      <Helmet>
        <title>SkyBMS - {t("title.tenantIndex")}</title>
      </Helmet>
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <div className="text-white font-semibold text-lg">
          <Typography
            component={Link}
            to="/"
            variant="h6"
            sx={{ flexGrow: 1, minWidth: "200px" }}
          >
            SkyBMS
          </Typography>
        </div>
        <div className="ml-4">
          <div className="text-white text-sm">
            {t("tenantUsers.welcome")}, {userInfo.user.full_name}
          </div>
        </div>
        <div className="flex items-center">
          <div className="mr-4">
            <select
              value={language}
              onChange={handleChangeLanguage}
              className="bg-white border border-gray-300 px-3 py-2 rounded-md text-sm font-medium text-gray-700"
            >
              <option value="en">En</option>
              <option value="አማ">አማ</option>
              <option value="or">orm</option>
            </select>
          </div>
          <IconButton
            className="text-white  bg-transparent border border-white rounded mr-4"
            onClick={handleNotificationClick}
          >
            <Badge badgeContent={unreadCount} color="secondary">
              <NotificationsIcon style={{ color: "#fff" }} />
            </Badge>
          </IconButton>

          {logoutLoading ? (
            <ClipLoader
              color="#fff"
              loading={logoutLoading}
              data-testid="loader"
              size={30}
            />
          ) : (
            <Button
              style={{ color: "#fff" }}
              startIcon={<ExitToAppIcon />}
              onClick={handleLogout}
            >
              {t("navbar.logout")}
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

const Notifications = ({ notifications, onClose }) => {
  const { t } = useTranslation();

  const renderNotificationMessage = (notification) => {
    let messageKey;
    let messageData;

    switch (notification.type) {
      case "Maintenance Request Created":
        try {
          const parsedData = JSON.parse(notification.data);
          messageData =
            parsedData?.notification_message ||
            "Default maintenance request message";
          messageKey = "notification.maintenanceRequestCreated";
        } catch (e) {
          console.error("Error parsing notification data:", e);
          messageData = "Error in maintenance request data";
          messageKey = "notification.error";
        }
        break;

      case "Tenant Payment Created":
        messageData = notification.data;
        messageKey = "notification.tenantPaymentCreated";
        break;

      case "Tenant Payment Attached":
        messageData = notification.data;
        messageKey = "notification.tenantPaymentAttached";
        break;

      case "Tenant Payment Approved":
        messageData = notification.data;
        messageKey = "notification.tenantPaymentApproved";
        break;

      case "Tenant Payment Rejected":
        messageData = notification.data;
        messageKey = "notification.tenantPaymentRejected";
        break;

      case "Lease Expired":
        messageData = notification.data;
        messageKey = "notification.tenantLeaseExpired";
        break;
      case "Lease Expiration Warning (2 Days)":
        messageData = notification.data;
        messageKey = "notification.tenantLeaseExpiredAfterTwoDays";
        break;
      case "Lease Expiration Warning (1 Week)":
        messageData = notification.data;
        messageKey = "notification.tenantLeaseExpiredAfterOneWeek";
        break;

      case "Lease Expiration Warning (1 Month)":
        messageData = notification.data;
        messageKey = "notification.tenantLeaseExpiredAfterOneMonth";
        break;

      case "Payment Due Today":
        messageData = notification.data;
        messageKey = "notification.tenantPaymentDueToday";
        break;

      case "Payment Due Soon (2 Days)":
        messageData = notification.data;
        messageKey = "notification.tenantPaymentDueTwoDays";
        break;

      case "Payment Due Soon (1 Week)":
        messageData = notification.data;
        messageKey = "notification.tenantPaymentDueOneWeek";
        break;

      default:
        messageData = "Unknown notification type";
        messageKey = "notification.unknown";
    }

    return t(messageKey, { unitNumber: messageData });
  };
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
      <div className="bg-white p-4 rounded-lg shadow-lg max-w-md">
        <h2 className="text-lg font-semibold mb-4">
          {t("notification.notification")}
        </h2>
        <ul className="divide-y divide-gray-300">
          {notifications.length !== 0 ? (
            notifications.map((notification, index) => (
              <li key={index} className="py-2">
                {renderNotificationMessage(notification)}
              </li>
            ))
          ) : (
            <li className="py-2">{t("notification.noNotification")}</li>
          )}
        </ul>
        <div className="mt-4 text-right">
          <button
            className="text-gray-600 hover:text-gray-800"
            onClick={onClose}
          >
            {t("notification.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

const LeaseStatus = ({
  status,
  startDate,
  endDate,
  buildingName,
  floor,
  unit,
}) => {
  const { t } = useTranslation();

  const getStatusBadge = () => {
    let colorClass = "";
    let text = "";

    switch (status.toLowerCase()) {
      case "new":
        colorClass = "bg-gray-500";
        text = t("tenantUsers.new");
        break;
      case "active":
        colorClass = "bg-green-500";
        text = t("tenantUsers.active");
        break;
      case "pending":
        colorClass = "bg-yellow-500";
        text = t("tenantUsers.pending");
        break;
      case "expired":
        colorClass = "bg-red-500";
        text = t("tenantUsers.expired");
        break;
      default:
        colorClass = "bg-gray-500";
        text = t("tenantUsers.unknown");
        break;
    }

    return { colorClass, text };
  };

  const { colorClass, text } = getStatusBadge();
  const buildingInfo =
    buildingName && floor && unit
      ? `${buildingName}, ${floor} - ${unit}`
      : "Building Information Unavailable";

  return (
    <div className="max-w-xs w-full bg-white shadow-lg rounded-lg overflow-hidden m-4">
      <div className={`px-4 py-2 ${colorClass} flex justify-between`}>
        <h2 className="text-lg font-semibold text-white">{buildingInfo}</h2>

        <h2 className="text-lg font-semibold text-white">{text}</h2>
      </div>
      <div className="px-4 py-2">
        <p className="text-gray-800 text-sm">
          <span className="font-semibold">{t("tenantUsers.leaseStatus")}:</span>{" "}
          {text}
        </p>
        {startDate && (
          <p className="text-gray-600 text-xs mt-1">
            <span className="font-semibold">{t("tenantUsers.startDate")}:</span>{" "}
            {startDate}
          </p>
        )}
        {endDate && (
          <p className="text-gray-600 text-xs mt-1">
            <span className="font-semibold">{t("tenantUsers.endDate")}:</span>{" "}
            {endDate}
          </p>
        )}
        <p className="text-gray-600 text-xs mt-1">
          {t("tenantUsers.currently")} {text.toLowerCase()}.
        </p>
        {status.toLowerCase() === "pending" && (
          <p className="text-yellow-600 text-xs mt-1">
            {t("tenantUsers.waitForApproval")}
          </p>
        )}
        {status.toLowerCase() === "expired" && (
          <p className="text-red-600 text-xs mt-1">
            {t("tenantUsers.contactYourLandlord")}
          </p>
        )}
      </div>
    </div>
  );
};

const PaymentStatus = ({
  payments,
  setSnackbarOpen,
  setSnackbarMessage,
  setSnackbarType,
  fetchLeases,
}) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [slip, setSlip] = useState(null);
  const openModal = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setInvoiceNumber("");
  };

  const handlePayButtonClick = (payment) => {
    openModal(payment);
  };

  const handleInvoiceNumberChange = (event) => {
    setInvoiceNumber(event.target.value);
  };
  const handleFileUpload = (e) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setSlip(selectedFile);
      };
      reader.readAsDataURL(selectedFile);
    }
  };
  const getStatusBadge = (status) => {
    let colorClass = "";
    let text = "";

    switch (status.toLowerCase()) {
      case "paid":
        colorClass = "bg-green-500";
        text = t("tenantUsers.paid");
        break;
      case "pending":
        colorClass = "bg-yellow-500";
        text = t("tenantUsers.pending");
        break;
      case "late":
        colorClass = "bg-red-500";
        text = t("tenantUsers.late");
        break;
      case "canceled":
        colorClass = "bg-gray-500";
        text = t("tenantUsers.canceled");
        break;
      case "awaiting approval":
        colorClass = "bg-blue-500";
        text = t("tenantUsers.awaitingApproval");
        break;
      default:
        colorClass = "bg-gray-500";
        text = t("tenantUsers.unknown");
        break;
    }

    return { colorClass, text };
  };
  return (
    <div className="max-w-xs w-full bg-white shadow-lg rounded-lg overflow-hidden m-4">
      <div className={`px-4 py-2 bg-blue-500`}>
        <h2 className="text-lg font-semibold text-white">
          {t("tenantUsers.paymentStatus")}
        </h2>
      </div>
      <div className="px-4 py-2 overflow-y-auto" style={{ maxHeight: "400px" }}>
        {payments
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .map((payment, index) => (
            <div key={index} className="mb-4">
              <div
                className={`px-4 py-2 flex items-center justify-between ${
                  getStatusBadge(payment.status).colorClass
                }`}
              >
                {payment.end_month ? (
                  <h3 className="text-md font-semibold text-white">
                    {" "}
                    {new Intl.DateTimeFormat("en-US", {
                      month: "long",
                    }).format(new Date(payment.start_month))}{" "}
                    {" - "}
                    {new Intl.DateTimeFormat("en-US", {
                      month: "long",
                    }).format(new Date(payment.end_month))}
                  </h3>
                ) : (
                  <h3 className="text-md font-semibold text-white">
                    {new Intl.DateTimeFormat("en-US", {
                      month: "long",
                    }).format(new Date(payment.start_month))}
                  </h3>
                )}
                <h3 className="text-md font-semibold text-white">
                  {getStatusBadge(payment.status).text}
                </h3>

                {(payment.status.toLowerCase() === "pending" ||
                  payment.status.toLowerCase() === "canceled") && (
                  <Tooltip title={t("tenantUsers.pay")}>
                    <IconButton
                      aria-label="pay"
                      onClick={() => handlePayButtonClick(payment)}
                    >
                      <AccountBalanceWalletIcon className="text-white" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              {/* Pay Icon */}

              <div>
                <p className="text-gray-600 text-xs mt-1">
                  <span className="font-semibold">
                    {" "}
                    {t("tenantUsers.rentFee")}:
                  </span>{" "}
                  {payment.rent_fee}
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  <span className="font-semibold">
                    {" "}
                    {t("tenantUsers.utilityFee")}:
                  </span>{" "}
                  {payment.utility_fee}
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  <span className="font-semibold">
                    {" "}
                    {t("tenantUsers.otherFee")}:
                  </span>{" "}
                  {payment.other_fee}
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  <span className="font-semibold">
                    {" "}
                    {t("tenantUsers.penaltyFee")}:
                  </span>{" "}
                  {payment.penalty_fee}
                </p>

                <p className="text-gray-600 text-xs mt-1">
                  <span className="font-semibold">
                    {" "}
                    {t("tenantUsers.totalFee")}:
                  </span>{" "}
                  {parseFloat(payment.rent_fee ?? 0) +
                    parseFloat(payment.utility_fee ?? 0) +
                    parseFloat(payment.other_fee ?? 0) +
                    parseFloat(payment.penalty_fee ?? 0)}
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  <span className="font-semibold">
                    {" "}
                    {t("tenantUsers.from")}:
                  </span>{" "}
                  {new Intl.DateTimeFormat("en-US", {
                    month: "long",
                    year: "numeric",
                  }).format(new Date(payment.start_month))}
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  <span className="font-semibold"> {t("tenantUsers.to")}:</span>{" "}
                  {payment.end_month !== null &&
                    new Intl.DateTimeFormat("en-US", {
                      month: "long",
                      year: "numeric",
                    }).format(new Date(payment.end_month))}
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  <span className="font-semibold">
                    {" "}
                    {t("tenantUsers.dueDate")}:
                  </span>{" "}
                  {new Date(payment.due_date).toLocaleDateString()}
                </p>
              </div>

              {payment.status.toLowerCase() === "pending" && (
                <p className="text-yellow-600 text-xs mt-1">
                  {t("tenantUsers.paymentIsPending")}
                </p>
              )}
              {payment.status.toLowerCase() === "late" && (
                <p className="text-red-600 text-xs mt-1">
                  {t("tenantUsers.paymentIsLate")}
                </p>
              )}
              {payment.status.toLowerCase() === "paid" && (
                <p className="text-green-600 text-xs mt-1">
                  {t("tenantUsers.paymentSuccessfullyReceived")}
                </p>
              )}
              {payment.status.toLowerCase() === "awaiting approval" && (
                <p className="text-blue-600 text-xs mt-1">
                  {t("tenantUsers.paymentAwaitingApproval")}
                </p>
              )}
              {payment.status.toLowerCase() === "canceled" && (
                <p className="text-gray-600 text-xs mt-1">
                  {t("tenantUsers.paymentCanceled")}
                </p>
              )}
              <hr className="my-1" />
            </div>
          ))}
      </div>
      {/* Modal for entering invoice number */}
      {showModal && selectedPayment && (
        <PaymentModal
          isOpen={showModal}
          onClose={closeModal}
          payment={selectedPayment}
          invoiceNumber={invoiceNumber}
          slip={slip}
          handleFileUpload={handleFileUpload}
          onInvoiceNumberChange={handleInvoiceNumberChange}
          setSnackbarOpen={setSnackbarOpen}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarType={setSnackbarType}
          fetchLeases={fetchLeases}
        />
      )}
    </div>
  );
};

const PaymentModal = ({
  isOpen,
  onClose,
  payment,
  invoiceNumber,
  slip,
  handleFileUpload,
  onInvoiceNumberChange,
  setSnackbarOpen,
  setSnackbarMessage,
  setSnackbarType,
  fetchLeases,
}) => {
  const { t } = useTranslation();
  const handleConfirm = async () => {
    try {
      const requestBody = {
        invoice_number: invoiceNumber,
        slip: slip,
      };
      const response = await PaymentService.updatePayment(
        payment.id,
        requestBody
      );

      if (response.status === 200) {
        setSnackbarMessage(t("tenantUsers.paymentAdded"));
        setSnackbarType("success");

        setSnackbarOpen(true);
        onClose();
        fetchLeases();
      }
    } catch (error) {
      console.log(error.message);
      setSnackbarMessage(t("tenantUsers.failedToUpdatePayment"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      invoiceNumber = null;
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} sx={{ minWidth: 300 }}>
      <DialogTitle sx={{ backgroundColor: "#2196f3", color: "white" }}>
        {t("tenantUsers.invoiceInfo")}
      </DialogTitle>
      <DialogContent sx={{ padding: 2 }}>
        <TextField
          autoFocus
          margin="dense"
          id="invoice-number"
          label={t("tenantUsers.invoiceNumber")}
          type="text"
          required
          fullWidth
          sx={{ marginBottom: 2 }}
          value={invoiceNumber}
          onChange={onInvoiceNumberChange}
        />

        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 10 }}
        >
          <IconButton
            color="primary"
            onClick={() =>
              document.getElementById("invoice-slip-upload").click()
            }
            sx={{ marginRight: 1 }}
          >
            <AttachFileIcon />
          </IconButton>
          <div style={{ flex: 1 }}>
            {slip ? (
              <div>{slip.name}</div>
            ) : (
              <div>{t("tenantUsers.noFileChosen")}</div>
            )}
          </div>
          <input
            accept=".pdf,.jpg,.png"
            style={{ display: "none" }}
            id="invoice-slip-upload"
            type="file"
            required
            onChange={handleFileUpload}
          />
        </div>

        <div style={{ marginTop: 15, textAlign: "left" }}>
          <Button
            onClick={handleConfirm}
            color="primary"
            sx={{ marginRight: 1 }}
          >
            {t("tenantUsers.confirmPayment")}
          </Button>
          <Button onClick={onClose} color="primary">
            {t("tenantUsers.cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BuildingDetails = ({ lease }) => {
  const { t } = useTranslation();

  return (
    <div className="max-w-xs w-full bg-white shadow-lg rounded-lg overflow-hidden m-4 pb-6 mb-10">
      <div className="px-4 py-2 bg-blue-500">
        <h2 className="text-lg font-semibold text-white">
          {t("tenantUsers.unitDetails")}
        </h2>
      </div>
      <div className="px-4 py-2">
        <p className="text-gray-800 text-sm">
          <span className="font-semibold">{t("tenantUsers.address")}:</span>{" "}
          {lease.building_unit.building.address}
        </p>
        <p className="text-gray-600 text-xs mt-1">
          <span className="font-semibold">{t("tenantUsers.floorNo")}:</span>{" "}
          {lease.building_unit.floor_no}
        </p>
        <p className="text-gray-600 text-xs mt-1">
          <span className="font-semibold">{t("tenantUsers.unitNo")}:</span>{" "}
          {lease.building_unit.unit_number}
        </p>
        {lease.building_unit.price_per_meter_sq !== 0 ? (
          <p className="text-gray-600 text-xs mt-1">
            <span className="font-semibold">
              {t("tenantUsers.pricePerMeterSq")} :{" "}
            </span>{" "}
            {lease.building_unit.price_per_meter_sq}
          </p>
        ) : (
          <p className="text-gray-600 text-xs mt-1">
            <span className="font-semibold">
              {t("tenantUsers.monthlyRent")} :{" "}
            </span>

            {lease.monthly_rent}
          </p>
        )}
        {lease.building_unit.price_per_meter_sq !== 0 && (
          <>
            <p className="text-gray-600 text-xs mt-1">
              <span className="font-semibold">
                {t("tenantUsers.areaInSqm")} :
              </span>{" "}
              {lease.building_unit.area_in_sqm}
            </p>
            <p className="text-gray-600 text-xs mt-1">
              <span className="font-semibold">
                {t("tenantUsers.monthlyRent")} :
              </span>{" "}
              {lease.monthly_rent}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// Maintenance request component
const MaintenanceRequest = ({ onSubmit, unit }) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(description, unit);
    setDescription("");
  };

  return (
    <div className="max-w-xs w-full bg-white shadow-lg rounded-lg overflow-hidden m-4 mb-10">
      <div className="px-4 py-2 bg-blue-500">
        <h2 className="text-lg font-semibold text-white">
          {t("tenantUsers.maintenanceRequest")}
        </h2>
      </div>
      <div className="px-4 py-2">
        <form onSubmit={handleSubmit}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t("tenantUsers.maintenanceRequestDescription")}
            rows="4"
          ></textarea>
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 mt-2 rounded"
          >
            {t("tenantUsers.submitRequest")}
          </button>
        </form>
      </div>
    </div>
  );
};

// Main App component
const UserHome = () => {
  const { t, i18n } = useTranslation();

  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [state, setState] = useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });
  const { vertical, horizontal, open } = state;
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [leases, setLeases] = useState([]);
  const [userInfo, setUserInfo] = useState(() => {
    return JSON.parse(localStorage.getItem("regularuser")) || [];
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");
  const [value, setValue] = useState(0);

  const handleCloseNotifications = () => {
    setShowNotifications(false);
  };
  const navigate = useNavigate();

  const fetchLeases = async () => {
    setLoading(true);
    try {
      const leases = await LeaseService.getLeaseByUserId(userInfo.user.id);

      for (const lease of leases) {
        try {
          const payments = await PaymentService.getPaymentsByLeaseId(lease.id);
          lease.payments = payments;
        } catch (paymentError) {
          console.error(
            `Error fetching payments for lease ${lease.id}:`,
            paymentError
          );
          // Set payments to null or handle the error as needed
          lease.payments = null;
        }
      }

      const sortedResponse = leases.sort((a, b) => {
        if (a.created_at > b.created_at) {
          return -1;
        }
        // Add additional logic if needed for other cases of the sort
        return 0;
      });
      console.log(sortedResponse);

      setLeases(sortedResponse);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leases:", error);
      const newState = { vertical: "bottom", horizontal: "center" };
      setState({ ...newState, open: true });
      setSnackbarMessage(null);
      setError(error);
      setLoading(false);
    }
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    async function fetchUsers() {
      localStorage.setItem("regularuser", JSON.stringify(userInfo));
    }
    fetchUsers();
  }, [userInfo]);

  const handleLogout = () => {
    setLogoutLoading(true);
    try {
      AuthService.regularUserLogout();
      setTimeout(() => {
        navigate("/login");
        setLogoutLoading(false);
      }, 2000);
    } catch (error) {
      console.log(error);
      setLogoutLoading(false);
    }
  };

  const handleLanguageChange = (selectedLanguage) => {};

  const handleMaintenanceRequest = async (description, unit) => {
    try {
      const requestData = {
        building_unit_id: unit,
        description: description,
        status: "pending",
      };
      const response = await MaintenanceRequestService.createMaintenanceRequest(
        requestData
      );
      if (response.status === 201) {
        setSnackbarMessage(t("tenantUsers.requestSubmittedSuccessfully"));
        setSnackbarType("success");
        setSnackbarOpen(true);

        fetchLeases();
      }
      setNotifications([
        ...notifications,
        `Maintenance request: ${description}`,
      ]);
    } catch (error) {
      console.error("Error submitting maintenance request:", error);
      setNotifications([
        ...notifications,
        `Failed to submit maintenance request: ${description}`,
      ]);
    }
  };

  useEffect(() => {
    const checkLoggedIn = async () => {
      const loggedIn = AuthService.isUserLoggedIn();
      if (!loggedIn) {
        navigate("/login");
      }
    };
    checkLoggedIn();
    fetchLeases();
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const notification = await NotificationService.getNotificationById(
          userInfo.user?.id
        );
        setNotifications(notification);
        const unreadNotifications = notification.filter(
          (notification) => notification.read === "0"
        );
        setUnreadCount(unreadNotifications.length);
      } catch (error) {
        console.error("Error fetching notifications:", error.message);
      }
    };

    fetchNotifications();
  }, []);

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
    setUnreadCount(0);
    markAllNotificationsAsSeen();
  };

  const markAllNotificationsAsSeen = async () => {
    const updatedNotifications = notifications.map((notification) => ({
      ...notification,
      seen: true,
    }));
    try {
      await NotificationService.markNotificationAsRead(userInfo.user?.id);
      setNotifications(updatedNotifications);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div>
      <Navbar
        handleNotificationClick={handleNotificationClick}
        handleLogout={handleLogout}
        handleLanguageChange={handleLanguageChange}
        i18n={i18n}
        userInfo={userInfo}
        logoutLoading={logoutLoading}
        unreadCount={unreadCount}
      />
      {showNotifications && (
        <Notifications
          notifications={notifications}
          onClose={handleCloseNotifications}
        />
      )}
      <div className="max-w-7xl mx-auto p-4">
        {loading && <p>{t("tenantUsers.loadingLeases")}</p>}

        {error && <p> {error.message}</p>}
        <div className="flex flex-wrap">
          {leases.map((lease) => (
            <div key={lease.id} className="w-full md:w-1/2 lg:w-1/3 px-4 mb-4">
              <LeaseStatus
                status={lease.status}
                buildingName={lease.building_unit.building.name}
                floor={lease.building_unit.floor_no}
                unit={lease.building_unit.unit_number}
                startDate={new Date(lease.start_date).toLocaleDateString()}
                endDate={new Date(lease.end_date).toLocaleDateString()}
              />
              <PaymentStatus
                payments={lease.payments || []}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
                setSnackbarType={setSnackbarType}
                fetchLeases={fetchLeases}
              />
              <BuildingDetails lease={lease} />
              <MaintenanceRequest
                onSubmit={handleMaintenanceRequest}
                unit={lease.building_unit.id}
              />
            </div>
          ))}
        </div>
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <BottomNavigation
            showLabels
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue);
            }}
            className="bg-gray-200 py-4 z-0 "
          >
            <footer>
              <Container maxWidth="lg">
                <Typography
                  variant="body2"
                  align="center"
                  color="textSecondary"
                >
                  &copy; {new Date().getFullYear()} Jabulani Mehal Merkato PLC.
                  All rights reserved. Powered by{" "}
                  <a
                    href="https://skylinkict.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:no-underline"
                  >
                    skylinkict
                  </a>
                  .
                </Typography>
              </Container>
            </footer>
          </BottomNavigation>
        </Paper>
      </div>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <SnackbarContent
          style={{
            backgroundColor: snackbarType === "success" ? "#5cb85c" : "#f44336",
          }}
          message={snackbarMessage}
        />
      </Snackbar>
    </div>
  );
};

export default UserHome;
